import { useSyncExternalStore } from 'react'
import type { ChatUser } from '../types/chat'
import type { CallMediaType } from '../types/voice-call'
import type { CallEligibilityResult } from '@shared/calls/call-eligibility'

/**
 * A 1:1 call the caller tried to place against a target whose status warrants a
 * warning or block. The top-level <CallGateDialog/> renders this and offers
 * "Send a message" (always) and "Call anyway" (warn only); `proceed` re-runs the
 * original start bypassing the gate.
 */
export type PendingCallGate = {
  peer: ChatUser
  mediaType: CallMediaType
  conversationId?: string | null
  result: CallEligibilityResult
  /** Re-run the call start, bypassing eligibility (used by "Call anyway"). */
  proceed: () => void
}

let pending: PendingCallGate | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function getPendingCallGate(): PendingCallGate | null {
  return pending
}

export function setPendingCallGate(next: PendingCallGate | null): void {
  pending = next
  emit()
}

export function clearPendingCallGate(): void {
  if (pending === null) return
  pending = null
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function usePendingCallGate(): PendingCallGate | null {
  return useSyncExternalStore(subscribe, getPendingCallGate, getPendingCallGate)
}
